
import {
  defineComponent,
  reactive,
  Ref,
  ref,
  computed,
  ComputedRef,
  watch,
  onMounted,
  nextTick,
} from 'vue';
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import AutoComplete from 'primevue/autocomplete';
import InputSwitch from 'primevue/inputswitch';
import OverlayPanel from 'primevue/overlaypanel';
import { CountryCode, isValidPhoneNumber } from 'libphonenumber-js';
import { useI18n } from 'vue-i18n';
import {
  helpers,
} from '@vuelidate/validators';
import useVuelidate from '@vuelidate/core';
import FieldErrors from '@/components/fields/partials/FieldErrors.vue';
import ContactListScroll from '@/components/contacts/ContactListScroll.vue';
import SendTestV4Preview from '@/components/modals/SendTestV4Preview.vue';
import SegmentationsPanel from '@/components/segmentations/SegmentationsPanel.vue';
// eslint-disable-next-line import/no-cycle
import { TemplateEditorState } from '@/composables/template-editor/TemplateEditor';
// eslint-disable-next-line import/no-cycle
import { useStore } from '@/store';
import {
  SaveShopsConfiguration,
  GetShopsConfigurationList,
} from '@/composables/shop/ShopsConfiguration';
import { Lang, UserState } from '@/composables/User';
import getShopsDomains from '@/composables/shop/ShopsSendDomains';
import {
  ShopsConfigurationInput,
  ShopsSendDomains,
} from '@/types/generated-types/graphql';
import { isValidEmail } from '@/helpers/Email';
import { getLocalStorageElement, setLocalStorageElement } from '@/helpers/LocalStorage';
import CountryDropdown from '@/components/fields/CountryDropdown.vue';
import RcsTestRecipientPanel from '@/components/rcs-test-send/RcsTestRecipientPanel.vue';
import { useRcsTestSend } from '@/composables/rcs/useRcsTestSend';

export interface Suggestion {
  key: string;
  value: string;
  country?: string;
}

interface SegmentCondition {
  id: number;
  config: Record<string, any>;
  key: string;
}

interface SegmentInfoPayload {
  hasSegments: boolean;
  conditions: Array<{ id: number; config: Record<string, any> }>;
  matchingCount: number;
}

export default defineComponent({
  name: 'SendTestV4Modal',

  components: {
    Dialog,
    Button,
    AutoComplete,
    InputSwitch,
    OverlayPanel,
    FieldErrors,
    ContactListScroll,
    SendTestV4Preview,
    SegmentationsPanel,
    CountryDropdown,
    RcsTestRecipientPanel,
  },

  props: {
    channel: {
      type: String,
      default: '',
    },
    rcsSenderId: {
      type: String,
      default: '',
    },
  },

  emits: {
    'on-validate': null,
  },

  setup(props, context) {
    const { t } = useI18n();
    const store = useStore();
    const displayModal: ComputedRef<boolean> = computed(() => store.state.general.sendTestEmailV4ModalVisible);
    const activeChannel = computed<'email' | 'sms' | 'whatsapp' | 'richtexting'>(() => {
      if (props.channel === 'sms') return 'sms';
      if (props.channel === 'whatsapp') return 'whatsapp';
      if (props.channel === 'richtexting') return 'richtexting';
      if (props.channel === 'email') return 'email';
      if (TemplateEditorState.template?.type === 'sms') return 'sms';
      if (TemplateEditorState.template?.type === 'whatsapp') return 'whatsapp';
      if (TemplateEditorState.template?.type === 'richtexting') return 'richtexting';
      return 'email';
    });
    const isEmailChannel = computed(() => activeChannel.value === 'email');
    const isSmsChannel = computed(() => activeChannel.value === 'sms');
    const isWhatsappChannel = computed(() => activeChannel.value === 'whatsapp');
    const isRcsChannel = computed(() => activeChannel.value === 'richtexting');
    const isPhoneChannel = computed(() => ['sms', 'whatsapp', 'richtexting'].includes(activeChannel.value));

    const rcsTestSend = useRcsTestSend();
    const rcsRecipientError = ref<string | null>(null);
    const rcsSenderOptions = computed(() => rcsTestSend.allSenderOptions.value);
    const rcsSelectedSenderId = computed(() => rcsTestSend.selectedSenderId.value);
    const rcsSelectedAgent = computed(() => rcsTestSend.selectedAgent.value);
    const rcsAvailableTestNumbers = computed(() => rcsTestSend.availableTestNumbers.value);
    const rcsSelectedTestNumbers = computed(() => rcsTestSend.selectedTestNumbers.value);
    const rcsSessionExtraNumbers = computed(() => rcsTestSend.sessionExtraNumbers.value);
    const rcsIsSharedSender = computed(() => rcsTestSend.isSharedSenderSelected.value);
    const rcsCanPersistTestNumbers = computed(() => rcsTestSend.canPersistTestNumbers.value);
    const rcsIsLoading = computed(() => rcsTestSend.isLoading.value);
    const rcsHasSelectedTestNumbers = computed(
      () => rcsTestSend.selectedTestNumbers.value.length > 0,
    );

    const initRcsTestSendState = () => {
      if (!isRcsChannel.value) return;
      rcsTestSend.prepareForModal(props.rcsSenderId || null);
    };
    const placeholderKey = computed(() => (
      isEmailChannel.value
        ? t('templateBuilder.modals.sendTestV4.sendPlaceholder')
        : t(`templateBuilder.modals.sendTest.placeholder${activeChannel.value}`)
    ));
    const channelIconClass = computed(() => {
      if (isRcsChannel.value) return 'fa-regular fa-message-smile';
      return isPhoneChannel.value ? 'fa-light fa-message' : 'fa-light fa-paper-plane';
    });

    // ========== Template ID ==========
    const templateId = computed(() =>
      TemplateEditorState.template?.id || store.state.general.sendTestEmailV4TemplateId || undefined,
    );

    const availableShopLangs = computed<Lang[]>(() => {
      const langs = UserState.activeShop?.langs ?? [];
      const sorted = [...langs];
      sorted.sort((a, b) => {
        if (a.default) return -1;
        if (b.default) return 1;
        return a.id.localeCompare(b.id);
      });
      return sorted;
    });

    const defaultShopLanguage = computed<string>(() => (
      availableShopLangs.value.find((language: Lang) => language.default)?.id
      ?? availableShopLangs.value[0]?.id
      ?? 'fr'
    ));

    const selectedLangs: Ref<string[]> = ref([]);
    const previewLang = ref('');
    const languageSelectionError = ref(false);
    const segmentConditions: Ref<SegmentCondition[]> = ref([]);
    const conditionOverrides = ref<Record<string, boolean>>({});
    const pendingStoredConditionOverrides = ref<Record<string, boolean> | null>(null);
    const conditionOverlayRef = ref();
    const activeConditionPopover = ref<SegmentCondition | null>(null);
    let conditionPopoverHideTimeout: ReturnType<typeof setTimeout> | null = null;

    const getAvailableLangIds = () => availableShopLangs.value.map((language: Lang) => language.id);
    const getConditionKey = (config: Record<string, any>) => JSON.stringify(config);

    const normalizeSelectedLangs = (langs: unknown): string[] => {
      const availableLangIds = getAvailableLangIds();
      if (!Array.isArray(langs)) return [];
      const uniqueLangs = Array.from(new Set(langs.filter((lang) => typeof lang === 'string'))) as string[];
      return uniqueLangs.filter((lang) => availableLangIds.includes(lang));
    };

    const applyLanguageSelection = (langs?: unknown, currentPreviewLang?: unknown) => {
      const availableLangIds = getAvailableLangIds();
      if (availableLangIds.length === 0) {
        selectedLangs.value = ['fr'];
        previewLang.value = 'fr';
        return;
      }

      const restoredLangs = normalizeSelectedLangs(langs);
      selectedLangs.value = restoredLangs.length > 0 ? restoredLangs : [defaultShopLanguage.value];

      if (typeof currentPreviewLang === 'string' && selectedLangs.value.includes(currentPreviewLang)) {
        previewLang.value = currentPreviewLang;
      } else {
        previewLang.value = selectedLangs.value[0];
      }
      languageSelectionError.value = false;
    };

    const toggleLanguage = (langId: string) => {
      if (selectedLangs.value.includes(langId)) {
        const remainingLangs = selectedLangs.value.filter((lang) => lang !== langId);
        if (remainingLangs.length === 0) {
          selectedLangs.value = [defaultShopLanguage.value];
          previewLang.value = defaultShopLanguage.value;
          languageSelectionError.value = false;
          return;
        }
        selectedLangs.value = remainingLangs;
      } else {
        selectedLangs.value = [...selectedLangs.value, langId];
      }

      if (!selectedLangs.value.includes(previewLang.value)) {
        previewLang.value = selectedLangs.value[0] ?? '';
      }

      if (selectedLangs.value.length > 0) {
        languageSelectionError.value = false;
      }
    };

    const onPreviewLangChanged = (langId: string) => {
      if (selectedLangs.value.includes(langId)) {
        previewLang.value = langId;
      }
    };

    const validateLanguageSelection = () => {
      if (selectedLangs.value.length === 0) {
        languageSelectionError.value = true;
        return false;
      }
      languageSelectionError.value = false;
      return true;
    };

    // ========== LocalStorage helpers (scoped per shop/user) ==========
    const getStorageKey = () => {
      const id = templateId.value;
      return id ? `sendTestV4_${id}` : null;
    };

    const saveToStorage = () => {
      const key = getStorageKey();
      if (!key) return;
      const data: Record<string, any> = {};
      if (selectedContactId.value) data.contactId = selectedContactId.value;
      if (selectedContactInfo.value) data.contactInfo = selectedContactInfo.value;
      if (isEmailChannel.value && Array.isArray(fieldValue.value) && fieldValue.value.length > 0) {
        data.emails = fieldValue.value.map((s: Suggestion) => s.value);
      }
      if (
        isPhoneChannel.value
        && !Array.isArray(fieldValue.value)
        && typeof fieldValue.value !== 'string'
        && fieldValue.value?.value
      ) {
        data.phone = fieldValue.value.value;
        data.country = countryAndState.value.country;
      }
      if (selectedLangs.value.length > 0) {
        data.langs = selectedLangs.value;
      }
      if (previewLang.value) {
        data.previewLang = previewLang.value;
      }
      if (Object.keys(conditionOverrides.value).length > 0) {
        data.conditionOverrides = conditionOverrides.value;
      }
      setLocalStorageElement(key, JSON.stringify(data));
    };

    const restoreFromStorage = () => {
      const key = getStorageKey();
      if (!key) {
        applyLanguageSelection();
        return;
      }
      try {
        const raw = getLocalStorageElement(key);
        if (!raw) {
          applyLanguageSelection();
          return;
        }
        const data = JSON.parse(raw);
        if (data.contactId) selectedContactId.value = Number(data.contactId);
        if (data.contactInfo) selectedContactInfo.value = data.contactInfo;
        if (isEmailChannel.value && Array.isArray(data.emails) && data.emails.length > 0) {
          fieldValue.value = data.emails.map((e: string) => ({ key: e, value: e }));
        }
        if (isPhoneChannel.value && data.phone) {
          fieldValue.value = { key: data.phone, value: data.phone };
          if (typeof data.country === 'string') {
            countryAndState.value.country = data.country as CountryCode;
          }
        }
        if (data.conditionOverrides && typeof data.conditionOverrides === 'object') {
          pendingStoredConditionOverrides.value = data.conditionOverrides;
        }
        applyLanguageSelection(data.langs, data.previewLang);
      } catch {
        // Ignore parse errors
        applyLanguageSelection();
      }
    };

    const synchronizeConditionOverrides = (conditions: SegmentCondition[]) => {
      const nextOverrides: Record<string, boolean> = {};
      conditions.forEach((condition) => {
        if (Object.prototype.hasOwnProperty.call(conditionOverrides.value, condition.key)) {
          nextOverrides[condition.key] = conditionOverrides.value[condition.key];
        } else if (
          pendingStoredConditionOverrides.value
          && Object.prototype.hasOwnProperty.call(pendingStoredConditionOverrides.value, condition.key)
        ) {
          nextOverrides[condition.key] = pendingStoredConditionOverrides.value[condition.key];
        } else {
          nextOverrides[condition.key] = true;
        }
      });
      conditionOverrides.value = nextOverrides;
      pendingStoredConditionOverrides.value = null;
    };

    const onSegmentInfoUpdate = (segmentInfo: SegmentInfoPayload) => {
      if (!segmentInfo?.hasSegments || !Array.isArray(segmentInfo.conditions)) {
        segmentConditions.value = [];
        conditionOverrides.value = {};
        return;
      }

      const mappedConditions: SegmentCondition[] = segmentInfo.conditions.map((condition) => ({
        id: condition.id,
        config: condition.config,
        key: getConditionKey(condition.config),
      }));
      segmentConditions.value = mappedConditions;
      synchronizeConditionOverrides(mappedConditions);
    };

    const setConditionOverride = (condition: SegmentCondition, value: boolean) => {
      conditionOverrides.value = {
        ...conditionOverrides.value,
        [condition.key]: value,
      };
    };

    const showConditionPopover = (event: Event, condition: SegmentCondition) => {
      if (conditionPopoverHideTimeout) {
        clearTimeout(conditionPopoverHideTimeout);
        conditionPopoverHideTimeout = null;
      }
      activeConditionPopover.value = condition;
      conditionOverlayRef.value?.show(event);
    };

    const hideConditionPopover = () => {
      conditionPopoverHideTimeout = setTimeout(() => {
        conditionOverlayRef.value?.hide();
        activeConditionPopover.value = null;
      }, 150);
    };

    // ========== Contact Selection ==========
    const selectedContactId: Ref<number | null> = ref(null);
    const selectedContactInfo: Ref<{ name: string; email: string } | null> = ref(null);
    const previewKey = ref(0);

    const onContactSelected = (contactId: number) => {
      selectedContactId.value = contactId;
    };

    const onContactInfoUpdate = (info: { name: string; email: string } | null) => {
      selectedContactInfo.value = info;
    };

    // ========== Email Autocomplete ==========
    const fieldValue: Ref<Suggestion | Suggestion[] | string | null> = ref(null);
    const dataSource: Ref<object[]> = ref([]);
    const domainsOfShop: Ref<ShopsSendDomains[]> = ref([]);
    const autocompleteRef = ref();
    const sendTestItems: Ref<Suggestion[]> = ref([]);

    const countryAndState = ref<{ country: CountryCode; state: string }>({
      country: (UserState.user.country ?? 'FR').toUpperCase() as CountryCode,
      state: '',
    });

    const getItems = async (): Promise<Suggestion[]> => {
      const key = isEmailChannel.value ? 'select2_list_emails_to_address' : 'select2_list_sms_to_number';
      const data = await GetShopsConfigurationList({
        shopId: UserState.activeShop?.id ?? 0,
        key,
        fields: 'id_shop_configuration, key, value',
        limit: 0,
        offset: 0,
      });

      const suggestions: Suggestion[] = [];
      if (data && data.length && data[0].value !== '') {
        const json = JSON.parse(data[0].value);

        if (json) {
          json.forEach((element: string) => {
            suggestions.push({
              key: element,
              value: element,
            });
          });
        }
      }

      return suggestions;
    };

    const search = (event: any) => {
      const current: string[] = [];

      if (isEmailChannel.value && Array.isArray(fieldValue.value)) {
        fieldValue.value.forEach((item) => {
          current.push(item.value);
        });
      } else if (isEmailChannel.value && typeof fieldValue.value === 'string' && fieldValue.value.trim()) {
        current.push(fieldValue.value.trim());
      } else if (
        isPhoneChannel.value
        && !Array.isArray(fieldValue.value)
        && typeof fieldValue.value !== 'string'
        && fieldValue.value?.value
      ) {
        current.push(fieldValue.value.value);
      } else if (isPhoneChannel.value && typeof fieldValue.value === 'string' && fieldValue.value.trim()) {
        current.push(fieldValue.value.trim().replace(new RegExp('[^0-9]', 'g'), ''));
      }

      if (!event.query.trim().length) {
        dataSource.value = [...sendTestItems.value];
      } else {
        dataSource.value = sendTestItems.value.filter(
          (item) => item.value.toLowerCase().startsWith(event.query.toLowerCase()) && !current.includes(item.value),
        );
      }

      const currentSearch: string = event.query;
      if (isEmailChannel.value) {
        const explode = currentSearch.split('@');
        const after = explode.length > 1 ? explode.pop() : '';
        const before = explode.join('@');

        domainsOfShop.value.forEach((domain: ShopsSendDomains) => {
          const returnValue = `${before}@${domain.domain}`;
          const found = dataSource.value.findIndex((el) => (el as Suggestion).value === returnValue);

          if (!current.includes(returnValue) && found === -1 && ((after && after.trim() === '') || (domain.domain.match(new RegExp(`^${after}`))))) {
            dataSource.value.unshift({
              key: returnValue,
              value: returnValue,
            });
          }
        });

        if (dataSource.value.findIndex((el) => (el as Suggestion).value === currentSearch) === -1) {
          dataSource.value.unshift({
            key: currentSearch,
            value: currentSearch,
          });
        }
      } else {
        const phoneNumber = String(currentSearch || '').trim().replace(new RegExp('[^0-9]', 'g'), '');
        if (phoneNumber) {
          dataSource.value.push({
            key: phoneNumber,
            value: phoneNumber,
          });
        }
      }
    };

    const normalizePhoneSuggestion = (item: Suggestion | string | null): Suggestion | null => {
      if (!item) return null;
      if (typeof item === 'string') {
        const normalized = item.trim().replace(new RegExp('[^0-9]', 'g'), '');
        if (!normalized) return null;
        return {
          key: normalized,
          value: normalized,
        };
      }
      return item.value ? item : null;
    };

    const validateItems = (items: Suggestion | Suggestion[] | string | null) => {
      if (isRcsChannel.value && rcsHasSelectedTestNumbers.value) {
        return true;
      }
      if (isEmailChannel.value) {
        if (typeof items === 'string') {
          return isValidEmail(items.trim());
        }
        if (Array.isArray(items) && items.length > 0) {
          return items.every((item: Suggestion) => isValidEmail(item.value));
        }
        return false;
      }
      if (!Array.isArray(items)) {
        const phoneSuggestion = normalizePhoneSuggestion(items);
        if (phoneSuggestion?.value) {
          return isValidPhoneNumber(phoneSuggestion.value, countryAndState.value.country);
        }
      }
      return false;
    };

    const getValidationMessage = () => {
      if (isEmailChannel.value) {
        return t('templateBuilder.modals.sendTest.errorEmail');
      }
      if (isRcsChannel.value) {
        return t('templateBuilder.modals.sendTest.errorRichTexting');
      }
      return t(`templateBuilder.modals.sendTest.error${activeChannel.value === 'sms' ? 'Sms' : 'Whatsapp'}`);
    };

    const getInitialFieldValue = () => (
      isEmailChannel.value ? [] : null
    );

    const mapParamsForEmit = (): Suggestion[] | Suggestion | null => {
      if (isEmailChannel.value) {
        if (typeof fieldValue.value === 'string' && fieldValue.value.trim()) {
          return [{
            key: fieldValue.value.trim(),
            value: fieldValue.value.trim(),
          }];
        }
        if (Array.isArray(fieldValue.value) && fieldValue.value.length > 0) {
          return fieldValue.value;
        }
        return [];
      }
      if (!Array.isArray(fieldValue.value)) {
        const phoneSuggestion = normalizePhoneSuggestion(fieldValue.value);
        if (!phoneSuggestion) {
          return null;
        }
        return {
          ...phoneSuggestion,
          country: countryAndState.value.country,
        };
      }
      return null;
    };

    const state = reactive({
      fieldValue,
    });
    const rules = {
      fieldValue: {
        validateItems: helpers.withMessage(
          getValidationMessage(),
          validateItems,
        ),
      },
    };
    const v$ = useVuelidate(rules, state);

    const upsertSendTestItemsConfig = async () => {
      const newConfigurationValue: string[] = [];
      sendTestItems.value.forEach((item: Suggestion) => {
        newConfigurationValue.push(item.value);
      });

      let insert = false;
      if (isEmailChannel.value && Array.isArray(fieldValue.value)) {
        fieldValue.value.forEach((input: Suggestion) => {
          if (!newConfigurationValue.includes(input.value)) {
            newConfigurationValue.push(input.value);
            insert = true;
          }
        });
      } else if (isPhoneChannel.value && !Array.isArray(fieldValue.value)) {
        const phoneSuggestion = normalizePhoneSuggestion(fieldValue.value);
        if (phoneSuggestion && !newConfigurationValue.includes(phoneSuggestion.value)) {
          newConfigurationValue.push(phoneSuggestion.value);
          insert = true;
        }
      }

      if (!insert || newConfigurationValue.length === 0) {
        return;
      }

      const key = isEmailChannel.value ? 'select2_list_emails_to_address' : 'select2_list_sms_to_number';
      const configuration: ShopsConfigurationInput = {
        id_shop: UserState.activeShop?.id ?? 0,
        configs: [
          {
            key,
            value: JSON.stringify(newConfigurationValue),
            lang: '',
          },
        ],
      };
      await SaveShopsConfiguration(configuration);
    };

    // ========== Modal Actions ==========
    const handleClose = () => {
      // Persist selections before resetting
      saveToStorage();
      fieldValue.value = getInitialFieldValue() as Suggestion[] | Suggestion | string | null;
      v$.value.$reset();
      store.commit('general/hideSendTestEmailV4Modal');
    };

    const canSendRcsTest = computed(() => {
      if (!isRcsChannel.value) return v$.value.$silentErrors.length === 0;
      if (rcsHasSelectedTestNumbers.value) return true;
      return v$.value.$silentErrors.length === 0;
    });

    const onRcsSenderChange = (senderId: string) => {
      rcsTestSend.selectedSenderId.value = senderId;
      rcsTestSend.selectedTestNumbers.value = [];
    };

    const onRcsTestNumbersChange = (numbers: string[]) => {
      rcsTestSend.selectedTestNumbers.value = numbers;
      rcsRecipientError.value = null;
    };

    const onRcsAddTestNumber = (number: string) => {
      rcsTestSend.addSessionTestNumber(number);
    };

    const onRcsPersistTestNumbers = async () => {
      await rcsTestSend.persistSessionTestNumbers();
    };

    const buildRcsParamsForEmit = (): Suggestion[] | Suggestion | null => {
      const phoneSuggestion = !Array.isArray(fieldValue.value)
        ? normalizePhoneSuggestion(fieldValue.value)
        : null;
      const recipient = phoneSuggestion
        ? { ...phoneSuggestion, country: countryAndState.value.country }
        : null;
      const built = rcsTestSend.buildRecipientParams(recipient, countryAndState.value.country);
      if (built.length === 0) {
        return null;
      }
      if (built.length === 1) {
        return built[0];
      }
      return built;
    };

    const handleValidation = async () => {
      if (!validateLanguageSelection()) {
        return;
      }

      rcsRecipientError.value = null;

      let params: Suggestion[] | Suggestion | null;
      if (isRcsChannel.value) {
        params = buildRcsParamsForEmit();
        if (!params) {
          rcsRecipientError.value = t('templateBuilder.modals.sendTestV4.rcs.recipientRequired');
          return;
        }
        if (!rcsHasSelectedTestNumbers.value) {
          const isValid = validateItems(fieldValue.value);
          if (!isValid) {
            await v$.value.$validate();
            return;
          }
        }
      } else {
        await upsertSendTestItemsConfig();
        params = mapParamsForEmit();
        if (!params) {
          return;
        }
      }

      store.commit('general/showTheSpinner');
      await new Promise<void>((resolve) => {
        const payload: Record<string, unknown> = {
          channel: activeChannel.value,
          params,
          contactId: selectedContactId.value,
          langs: selectedLangs.value,
          conditionOverrides: conditionOverrides.value,
          done: resolve,
        };
        if (isRcsChannel.value && rcsTestSend.selectedSenderId.value) {
          payload.senderId = rcsTestSend.selectedSenderId.value;
          payload.rcsSenderId = rcsTestSend.selectedSenderId.value;
        }
        context.emit('on-validate', payload);
      });
      store.commit('general/hideTheSpinner');

      saveToStorage();
    };

    onMounted(() => {
      if (displayModal.value) {
        getItems().then((data) => {
          sendTestItems.value = data;
        });

        getShopsDomains(0).then((data) => {
          domainsOfShop.value = data;
        });

        initRcsTestSendState();
        restoreFromStorage();
      }
    });

    watch(displayModal, (newVal) => {
      if (newVal) {
        getItems().then((data) => {
          sendTestItems.value = data;
        });

        getShopsDomains(0).then((data) => {
          domainsOfShop.value = data;
        });

        initRcsTestSendState();

        restoreFromStorage();
        previewKey.value++;

        nextTick().then(() => {
          if (autocompleteRef.value) {
            const input = autocompleteRef.value.$el.querySelector('.send-card__autocomplete-input');
            if (input) {
              setTimeout(() => {
                input.focus();
              }, 0);
            }
          }
        });
      }
    });

    watch(activeChannel, async (newChannel, oldChannel) => {
      if (newChannel === oldChannel) return;
      fieldValue.value = getInitialFieldValue() as Suggestion[] | Suggestion | string | null;
      v$.value.$reset();
      sendTestItems.value = await getItems();
      if (isEmailChannel.value) {
        domainsOfShop.value = await getShopsDomains(0);
      } else {
        domainsOfShop.value = [];
      }
      restoreFromStorage();
      previewKey.value += 1;
    });

    return {
      t,
      v$,
      displayModal,
      // Template & Contact selection
      templateId,
      selectedContactId,
      selectedContactInfo,
      previewKey,
      previewLang,
      selectedLangs,
      activeChannel,
      isEmailChannel,
      isSmsChannel,
      isWhatsappChannel,
      isRcsChannel,
      isPhoneChannel,
      rcsSenderOptions,
      rcsSelectedSenderId,
      rcsSelectedAgent,
      rcsAvailableTestNumbers,
      rcsSelectedTestNumbers,
      rcsSessionExtraNumbers,
      rcsIsSharedSender,
      rcsCanPersistTestNumbers,
      rcsIsLoading,
      rcsRecipientError,
      rcsHasSelectedTestNumbers,
      canSendRcsTest,
      onRcsSenderChange,
      onRcsTestNumbersChange,
      onRcsAddTestNumber,
      onRcsPersistTestNumbers,
      placeholderKey,
      channelIconClass,
      availableShopLangs,
      segmentConditions,
      conditionOverrides,
      languageSelectionError,
      onContactSelected,
      onContactInfoUpdate,
      onPreviewLangChanged,
      toggleLanguage,
      onSegmentInfoUpdate,
      setConditionOverride,
      conditionOverlayRef,
      activeConditionPopover,
      showConditionPopover,
      hideConditionPopover,
      // Email input
      dataSource,
      search,
      autocompleteRef,
      countryAndState,
      // Actions
      handleClose,
      handleValidation,
    };
  },
});
